/**
 * Fork of the default scaffolder template list so we can show a docs notice above the grid.
 * `ScaffolderPage` must stay the route `element` (routable extension), and upstream does not
 * export `TemplateListPage`; if that changes, replace this file with a thin wrapper.
 */
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import type { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import {
  Content,
  ContentHeader,
  DocsIcon,
  Header,
  Page,
  SupportButton,
} from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntitySearchBar,
  EntityTagPicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { scaffolderTranslationRef, type TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import {
  ScaffolderPageContextMenu,
  TemplateCategoryPicker,
  TemplateGroups,
} from '@backstage/plugin-scaffolder-react/alpha';
import { buildTechDocsURL } from '@backstage/plugin-techdocs-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import type { Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Alert from '@material-ui/lab/Alert';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import { usePermission } from '@backstage/plugin-permission-react';
import { useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useApp, useRouteRef } from '@backstage/core-plugin-api';

const TEMPLATES_DOC_URL =
  'https://backstage.inv.systems/docs/default/component/backstage/using-templates/';

/** Same keys as `@backstage/plugin-techdocs-common` (avoid a direct app dependency for lint). */
const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';
const TECHDOCS_EXTERNAL_ANNOTATION = 'backstage.io/techdocs-entity';

function TemplatesDocBanner() {
  return (
    <Box mb={2}>
      <Alert severity="info">
        New to Backstage templates? See the{' '}
        <Link
          href={TEMPLATES_DOC_URL}
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          underline="always"
        >
          Backstage template docs
        </Link>{' '}
        for what each template does and how to choose one.
      </Alert>
    </Box>
  );
}

/** Copied from upstream `RegisterExistingButton` (not exported from the plugin). */
function RegisterExistingButton(props: { title: string; to: string | undefined }) {
  const { title, to } = props;
  const { allowed } = usePermission({ permission: catalogEntityCreatePermission });
  const isXSScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  if (!to || !allowed) {
    return null;
  }
  return isXSScreen ? (
    <IconButton
      component={RouterLink}
      color="primary"
      title={title}
      size="small"
      to={to}
    >
      <CreateComponentIcon />
    </IconButton>
  ) : (
    <Button component={RouterLink} variant="outlined" color="primary" to={to}>
      {title}
    </Button>
  );
}

export function TemplateListPageWithDocsBanner(props: TemplateListPageProps) {
  const registerComponentLink = useRouteRef(scaffolderPlugin.externalRoutes.registerComponent);
  const {
    TemplateCardComponent,
    groups: givenGroups = [],
    templateFilter,
    headerOptions,
  } = props;
  const navigate = useNavigate();
  const editorLink = useRouteRef(scaffolderPlugin.routes.edit);
  const actionsLink = useRouteRef(scaffolderPlugin.routes.actions);
  const tasksLink = useRouteRef(scaffolderPlugin.routes.listTasks);
  const viewTechDocsLink = useRouteRef(scaffolderPlugin.externalRoutes.viewTechDoc);
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const templatingExtensionsLink = useRouteRef(scaffolderPlugin.routes.templatingExtensions);
  const app = useApp();
  const { t } = useTranslationRef(scaffolderTranslationRef);
  const groups = givenGroups.length
    ? [
        ...givenGroups,
        {
          title: t('templateListPage.templateGroups.otherTitle'),
          filter: (e: TemplateEntityV1beta3) =>
            ![...givenGroups].some(({ filter }) => filter(e)),
        },
      ]
    : [
        {
          title: t('templateListPage.templateGroups.defaultTitle'),
          filter: () => true,
        },
      ];
  const scaffolderPageContextMenuProps = {
    onEditorClicked: props?.contextMenu?.editor !== false ? () => navigate(editorLink()) : undefined,
    onActionsClicked: props?.contextMenu?.actions !== false ? () => navigate(actionsLink()) : undefined,
    onTasksClicked: props?.contextMenu?.tasks !== false ? () => navigate(tasksLink()) : undefined,
    onTemplatingExtensionsClicked:
      props?.contextMenu?.templatingExtensions !== false
        ? () => navigate(templatingExtensionsLink())
        : undefined,
  };
  const additionalLinksForEntity = useCallback(
    (template: TemplateEntityV1beta3) => {
      if (
        !(
          template.metadata.annotations?.[TECHDOCS_ANNOTATION] ||
          template.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]
        ) ||
        !viewTechDocsLink
      ) {
        return [];
      }
      const url = buildTechDocsURL(template, viewTechDocsLink);
      return url
        ? [
            {
              icon: app.getSystemIcon('docs') ?? DocsIcon,
              text: t('templateListPage.additionalLinksForEntity.viewTechDocsTitle'),
              url,
            },
          ]
        : [];
    },
    [app, viewTechDocsLink, t],
  );
  const onTemplateSelected = useCallback(
    (template: TemplateEntityV1beta3) => {
      const { namespace, name } = parseEntityRef(stringifyEntityRef(template));
      navigate(templateRoute({ namespace, templateName: name }));
    },
    [navigate, templateRoute],
  );

  return (
    <EntityListProvider>
      <Page themeId="home">
        <Header
          pageTitleOverride={t('templateListPage.pageTitle')}
          title={t('templateListPage.title')}
          subtitle={t('templateListPage.subtitle')}
          {...headerOptions}
        >
          <ScaffolderPageContextMenu {...scaffolderPageContextMenuProps} />
        </Header>
        <Content>
          <TemplatesDocBanner />
          <ContentHeader>
            <RegisterExistingButton
              title={t('templateListPage.contentHeader.registerExistingButtonTitle')}
              to={registerComponentLink && registerComponentLink()}
            />
            <SupportButton>{t('templateListPage.contentHeader.supportButtonTitle')}</SupportButton>
          </ContentHeader>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker initialFilter="template" hidden />
              <UserListPicker initialFilter="all" availableFilters={['all', 'starred']} />
              <TemplateCategoryPicker />
              <EntityTagPicker />
              <EntityOwnerPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <TemplateGroups
                groups={groups}
                templateFilter={templateFilter}
                TemplateCardComponent={TemplateCardComponent}
                onTemplateSelected={onTemplateSelected}
                additionalLinksForEntity={additionalLinksForEntity}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </Page>
    </EntityListProvider>
  );
}
